import type { GroupBy, GroupNode } from '@tive/core';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import { ArrowLeft, Plus, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { swatchFor } from '../lib/colors';
import { GROUP_CARD_WIDTH, groupCardHeight, layoutGroups, type GroupLayoutNode } from '../lib/group-layout';
import { buildGroupTree, groupingKey, resolveFolded, type GroupTree } from '../lib/group-tree';
import { resolveTheme } from '../lib/theme';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { FilterBanner } from './FilterBanner';
import { GroupCard, LEVEL_LABELS, type GroupCardData } from './GroupCard';
import { TrunkEdge } from './TrunkEdge';
import { Badge, Button, EmptyState, IconButton, Input } from './ui';

/** The order the group-by menus offer the levels in. */
const LEVEL_ORDER: GroupBy[] = ['org', 'department', 'squad', 'team', 'location', 'country'];

/**
 * The operational view: the same people, grouped by what they work on instead
 * of who they report to.
 *
 * It is the same pan-and-zoom canvas as the org chart, and it did not used to
 * be. Ordinary reflowing HTML was the right answer while the board was a
 * shallow outline of section headings with a row of team boxes under each —
 * it worked on a phone for free and needed none of the jsdom gymnastics React
 * Flow does. What it could not do is fit. A real org grouped three levels deep
 * with sub-teams opened is wider than any window, and a wrapping row has no way
 * to zoom out of that; boxes simply ran off the side.
 *
 * So every level of the grouping is a card now — org, department and team
 * alike — hanging off its parent the way a report hangs off a manager, with a
 * fold pill on its bottom edge. A grouping with several values at the top just
 * makes several trees, side by side.
 */

/*
 * `groupCard`, not `group`: React Flow has a built-in node type of that name for
 * container nodes, and even with the renderer overridden it still stamps
 * `.react-flow__node-group` on the element — whose default stylesheet drew a
 * second bordered box around every card.
 */
const nodeTypes = { groupCard: GroupCard };
const edgeTypes = { trunk: TrunkEdge };

/**
 * React Flow's canvas chrome is drawn to SVG and cannot read CSS variables, so
 * these few brand values are mirrored here, as they are in the org chart.
 */
const BRAND = {
  darkGray: '#404040',
  lineStrongLight: '#d4d4d4',
  dotsDark: '#2e2e2e',
  dotsLight: '#dcdcdc',
} as const;

type GroupFlowNode = Node<GroupCardData, 'groupCard'>;

export function TeamBoard() {
  return (
    // Its own provider, so the board's viewport is not the chart's: switching
    // tabs should not carry a zoom from one view into the other.
    <ReactFlowProvider>
      <TeamBoardInner />
    </ReactFlowProvider>
  );
}

function TeamBoardInner() {
  const model = useAppStore((s) => s.model);
  const levels = useAppStore((s) => s.groupLevels);
  const focusId = useAppStore((s) => s.focusId);
  const setFocus = useAppStore((s) => s.setFocus);
  const foldedGroups = useAppStore((s) => s.foldedGroups);
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');
  const showMinimap = useAppStore((s) => s.showMinimap);
  const exporting = useAppStore((s) => s.exporting);
  const fitSignal = useAppStore((s) => s.fitSignal);
  const colorOverrides = useAppStore((s) => s.colorOverrides);
  const { fitView } = useReactFlow();

  /*
   * Which teams are on screen follows which ones are being edited. Editing the
   * base while looking at the plan would let you take somebody off a team the
   * base never had them on, so the two move together.
   */
  const squads = useAppStore((s) => (s.teamEditTarget === 'base' ? s.squads : s.planSquads));

  const focusPerson = focusId && model ? model.byId.get(focusId) : null;

  // Annotated, or the empty fallback widens the map to `Map<any, any>` and
  // every read off a card below it is an `any`.
  const tree = useMemo<GroupTree>(
    () => (model ? buildGroupTree({ model, levels, squads, focusId }) : { nodes: new Map(), rootIds: [] }),
    [model, levels, squads, focusId],
  );

  const grouping = groupingKey(levels, focusId);
  const folded = useMemo(() => resolveFolded(tree, foldedGroups[grouping]), [tree, foldedGroups, grouping]);

  const layout = useMemo(() => layoutGroups(toLayoutNodes(tree.nodes), tree.rootIds, folded), [tree, folded]);

  const nodes = useMemo<GroupFlowNode[]>(
    () =>
      layout.visibleIds.flatMap((id) => {
        const node = tree.nodes.get(id);
        const placement = layout.positions.get(id);
        if (!node || !placement) return [];
        return [
          {
            id,
            type: 'groupCard' as const,
            position: { x: placement.x, y: placement.y },
            data: { node, childCount: node.childIds.length, folded: folded.has(id) },
            draggable: false,
            connectable: false,
            selectable: false,
            width: GROUP_CARD_WIDTH,
            height: groupCardHeight({ memberCount: node.members.length, childIds: node.childIds }),
          },
        ];
      }),
    [layout, tree, folded],
  );

  const edges = useMemo<Edge[]>(
    () =>
      layout.edges.map((edge) => ({
        id: edge.id,
        source: edge.parentId,
        target: edge.childId,
        sourceHandle: 'below',
        // A stacked card is entered from the side the trunk is on; one with a
        // place of its own gets the usual elbow into its top edge.
        targetHandle: edge.kind === 'stack' ? edge.side : 'above',
        type: 'trunk',
        data: { trunkX: edge.trunkX },
        selectable: false,
        focusable: false,
      })),
    [layout],
  );

  /*
   * Framed when the grouping changes, and only then.
   *
   * Switching levels builds a different tree, and the viewport that framed the
   * old one usually points at empty canvas in the new one — which reads as the
   * board having gone blank. Folding a card is not that: it is a deliberate
   * local act, the cards it reveals appear directly under the one just clicked,
   * and re-framing on every one of them would zoom the whole board out a step at
   * a time until nothing could be read. Same rule as the chart, which never
   * moves the viewport on an expand either.
   */
  const ready = nodes.length > 0;
  useEffect(() => {
    if (!ready) return;
    const frame = requestAnimationFrame(() => void fitView({ padding: 0.12, maxZoom: 1 }));
    return () => cancelAnimationFrame(frame);
    // `fitSignal` is the toolbar asking, which it does when everything folds:
    // what was filling the screen has gone, and the viewport is left pointing
    // at a corner of a canvas sized for the whole company.
  }, [grouping, ready, fitSignal, fitView]);

  const dotColor = dark ? BRAND.dotsDark : BRAND.dotsLight;

  return (
    <div className="flex h-full flex-col bg-[var(--color-canvas)]">
      <GroupControls />

      <div className="px-3 empty:hidden sm:px-5">
        <FilterBanner />
      </div>

      {focusPerson ? (
        <div className="px-3 pt-2 sm:px-5">
          <button
            type="button"
            onClick={() => setFocus(null)}
            className="flex items-center gap-2 text-[12px] text-[var(--color-ink-2)] hover:text-[var(--color-ink)]"
          >
            <ArrowLeft size={13} />
            Only {focusPerson.name}&apos;s team — show the whole company
          </button>
        </div>
      ) : null}

      {nodes.length === 0 ? (
        <EmptyState
          icon={<Users size={18} />}
          title="Nothing to group yet"
          hint="Pick at least one level to group by, or create a team and add people to it from their card."
        />
      ) : (
        <div className="min-h-0 flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            // A whole company grouped three deep is several thousand pixels
            // wide, so it has to be able to zoom a long way out.
            minZoom={0.05}
            maxZoom={1.6}
            zoomOnDoubleClick={false}
            nodesConnectable={false}
            nodesDraggable={false}
            elementsSelectable={false}
            panOnScroll
            selectionOnDrag={false}
            // Off while a PNG is being taken, or the export would have holes
            // where the off-screen cards should be.
            onlyRenderVisibleElements={nodes.length > 180 && !exporting}
            proOptions={{ hideAttribution: true }}
            colorMode={dark ? 'dark' : 'light'}
            className="bg-[var(--color-canvas)]"
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1.1} color={dotColor} />
            <Controls showInteractive={false} position="bottom-left" />
            {showMinimap ? (
              <MiniMap
                pannable
                zoomable
                position="bottom-right"
                className="max-sm:!hidden"
                maskColor={dark ? 'rgba(20,20,20,0.75)' : 'rgba(242,242,242,0.75)'}
                nodeColor={(node) => {
                  const data = node.data as GroupCardData | undefined;
                  if (data?.node.key !== 'squad') return dark ? BRAND.darkGray : BRAND.lineStrongLight;
                  return swatchFor(data.node.label, colorOverrides);
                }}
                style={{ width: 168, height: 118 }}
              />
            ) : null}
          </ReactFlow>
        </div>
      )}
    </div>
  );
}

/** Only the geometry, so the layout has nothing to be wrong about. */
function toLayoutNodes(nodes: ReadonlyMap<string, GroupNode>): Map<string, GroupLayoutNode> {
  const out = new Map<string, GroupLayoutNode>();
  for (const [id, node] of nodes) {
    out.set(id, { id, childIds: node.childIds, memberCount: node.members.length });
  }
  return out;
}

// ---------------------------------------------------------------------------

function GroupControls() {
  const levels = useAppStore((s) => s.groupLevels);
  const setGroupLevels = useAppStore((s) => s.setGroupLevels);
  const createSquad = useAppStore((s) => s.createSquad);
  const target = useAppStore((s) => s.teamEditTarget);
  const setTarget = useAppStore((s) => s.setTeamEditTarget);
  const sharedTeams = useAppStore((s) => s.sharedTeams);
  const scenarioName = useAppStore((s) => s.scenario?.name);
  const teamChanges = useAppStore((s) => s.summary.teamChanges);

  const squads = useAppStore((s) => (s.teamEditTarget === 'base' ? s.squads : s.planSquads).squads);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState('');
  const [parent, setParent] = useState('');

  // A "None" in the middle would leave a hole, so dropping a level drops
  // everything under it too.
  const setLevel = (index: number, value: GroupBy | '') => {
    const next = levels.slice(0, index);
    if (value) next.push(value, ...levels.slice(index + 1));
    setGroupLevels(next);
  };

  const submit = () => {
    const name = draft.trim();
    if (!name) return;
    void createSquad(name, undefined, parent || null);
    setDraft('');
    setParent('');
    setCreating(false);
  };

  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2.5 sm:px-5',
        // Tinted while the shared directory is what an edit will change: it is
        // the one mode where a click is not undoable from the change list.
        target === 'base'
          ? 'border-[var(--color-brand)]/30 bg-[var(--color-brand-soft)]'
          : 'border-[var(--color-line)] bg-[var(--color-surface)]',
      )}
    >
      {/*
        With published teams there is nothing to choose between: the base is one
        person's curation for the whole company, edited locally and published, and
        the server refuses to change it here. A toggle whose other half always
        errors is worse than no toggle.
      */}
      {sharedTeams ? null : (
        <div
          role="group"
          aria-label="What a team edit changes"
          className="flex shrink-0 items-center gap-0.5 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] p-0.5"
        >
          <TargetTab
            label="Base teams"
            hint="Edit how the teams actually are. Shared by every scenario."
            active={target === 'base'}
            onClick={() => setTarget('base')}
          />
          <TargetTab
            label={scenarioName ? `Plan: ${scenarioName}` : 'This plan'}
            hint="Propose team changes inside this scenario. They show up in the change list."
            active={target === 'scenario'}
            count={teamChanges}
            onClick={() => setTarget('scenario')}
          />
        </div>
      )}

      <span className="text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
        Group by
      </span>

      <div className="flex flex-wrap items-center gap-1.5">
        {[0, 1, 2].map((index) => (
          <LevelSelect
            key={index}
            index={index}
            value={levels[index] ?? ''}
            // Only ever offer the next empty slot, so the levels stay contiguous.
            disabled={index > levels.length}
            onChange={(value) => setLevel(index, value)}
          />
        ))}
      </div>

      <div className="ml-auto flex items-center gap-2">
        <span className="hidden text-[11px] text-[var(--color-ink-2)] sm:inline">
          {target === 'base' ? 'Editing the teams as they are today' : 'Proposing changes in this scenario'}
        </span>

        {creating ? (
          <div className="flex items-center gap-1.5">
            <Input
              autoFocus
              aria-label="Team name"
              placeholder="Artemis"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
                if (event.key === 'Escape') setCreating(false);
              }}
              className="h-8 w-40"
            />
            {/* A group of teams is itself a team, so a new one can be born
                inside another rather than having to be moved in afterwards. */}
            {squads.length > 0 ? (
              <select
                aria-label="Inside"
                value={parent}
                onChange={(event) => setParent(event.target.value)}
                className="h-8 max-w-[10rem] rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-ink)]"
              >
                <option value="">Top level</option>
                {squads.map((squad) => (
                  <option key={squad.id} value={squad.id}>
                    Inside {squad.name}
                  </option>
                ))}
              </select>
            ) : null}
            <Button size="sm" variant="primary" onClick={submit} disabled={draft.trim().length === 0}>
              Create
            </Button>
            <IconButton label="Cancel" className="size-8" onClick={() => setCreating(false)}>
              <X size={14} />
            </IconButton>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <Plus size={12} /> New team
          </Button>
        )}
      </div>
    </div>
  );
}

function TargetTab({
  label,
  hint,
  active,
  count,
  onClick,
}: {
  label: string;
  hint: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={hint}
      aria-pressed={active}
      className={cn(
        'flex h-7 max-w-[190px] items-center gap-1.5 rounded-md px-2 text-[12px] font-medium transition-colors',
        active
          ? 'bg-[var(--color-surface)] text-[var(--color-ink)] shadow-sm'
          : 'text-[var(--color-ink-3)] hover:text-[var(--color-ink-2)]',
      )}
    >
      <span className="truncate">{label}</span>
      {count ? <Badge tone="brand">{count}</Badge> : null}
    </button>
  );
}

function LevelSelect({
  index,
  value,
  disabled,
  onChange,
}: {
  index: number;
  value: GroupBy | '';
  disabled: boolean;
  onChange: (value: GroupBy | '') => void;
}) {
  return (
    <select
      aria-label={`Group level ${index + 1}`}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as GroupBy | '')}
      className={cn(
        'h-8 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] px-2 text-[12px]',
        'text-[var(--color-ink)] disabled:opacity-40',
      )}
    >
      <option value="">{index === 0 ? 'Nothing' : 'None'}</option>
      {LEVEL_ORDER.map((level) => (
        <option key={level} value={level}>
          {LEVEL_LABELS[level]}
        </option>
      ))}
    </select>
  );
}
